import { useCallback, useEffect, useMemo, useState } from 'react';
import queryString from 'query-string';
import { useReady } from '@shapeable/ui';

const isValidTabView = <T extends string>(
  value: unknown,
  validTabViews: readonly T[],
): value is T => (
  typeof value === 'string' && (validTabViews as readonly string[]).includes(value)
);

const viewFromLocationHash = <T extends string>(validTabViews: readonly T[]): T | undefined => {
  if (typeof window === 'undefined') {
    return undefined;
  }

  const parts = queryString.parse(window.location.hash.slice(1).replace(/;/g, '&'));
  return isValidTabView(parts.view, validTabViews) ? parts.view : undefined;
};

const replaceContentViewHash = <T extends string>(view: T) => {
  const hash = queryString.stringify({ view }).replace(/&/g, ';');
  const url = `${window.location.pathname}${window.location.search}#${hash}`;
  window.history.replaceState(null, '', url);
};

export type ContentViewControls<T extends string = string> = {
  activeView: T;
  setActiveView: (view: T) => void;
  showView: (view: T) => boolean;
};

/**
 * Entity content tabs (Topic, Event, etc.) — React state + replaceState instead of
 * location.hash so Gatsby does not remount the page on tab change.
 */
export const useContentView = <T extends string>(
  defaultView: T,
  validTabViews: readonly T[],
): ContentViewControls<T> => {
  const isReady = useReady();
  const [selectedView, setSelectedView] = useState<T | null>(null);

  const hashView = useMemo(
    () => (isReady ? viewFromLocationHash(validTabViews) : undefined),
    [isReady, validTabViews],
  );

  const activeView: T = (
    isReady && (selectedView ?? hashView ?? defaultView)
  ) || defaultView;

  useEffect(() => {
    const syncFromHash = () => {
      const fromHash = viewFromLocationHash(validTabViews);
      if (fromHash) {
        setSelectedView(fromHash);
      }
    };

    window.addEventListener('hashchange', syncFromHash);
    window.addEventListener('popstate', syncFromHash);
    return () => {
      window.removeEventListener('hashchange', syncFromHash);
      window.removeEventListener('popstate', syncFromHash);
    };
  }, [validTabViews]);

  const setActiveView = useCallback((view: T) => {
    setSelectedView(view);
    replaceContentViewHash(view);
  }, []);

  const showView = useCallback(
    (view: T) => !isReady || activeView === view,
    [activeView, isReady],
  );

  return { activeView, setActiveView, showView };
};
